import {
  useCallback,
  useRef,
  useState,
  type Dispatch,
  type RefObject,
  type SetStateAction
} from "react";
import { TextAreaInputHandle } from "../../../../../Forms/Inputs/TextArea/React";
import { TextInputHandle } from "../../../../../Forms/Inputs/TextInput/React";
import {
  Blurred,
  OnlineBookingClient,
  OnlineBookingClientValidation,
  OnlineBookingPatient,
  OnlineBookingPatientValidation,
  OnlineBookingValidation
} from "../../../types";

/**
 * Hook return type for useContactDetailsValidation.
 */
export interface UseContactDetailsValidationReturn {
  petNotesRef: RefObject<TextAreaInputHandle>;
  identificationNumberRef: RefObject<TextInputHandle>;
  client: OnlineBookingClient;
  setClient: Dispatch<SetStateAction<OnlineBookingClient>>;
  patient: OnlineBookingPatient;
  setPatient: Dispatch<SetStateAction<OnlineBookingPatient>>;
  clientValidation: OnlineBookingClientValidation;
  setClientValidation: Dispatch<SetStateAction<OnlineBookingClientValidation>>;
  patientValidation: OnlineBookingPatientValidation;
  setPatientValidation: Dispatch<SetStateAction<OnlineBookingPatientValidation>>;
  formValidated: boolean;
  setFormValidated: Dispatch<SetStateAction<boolean>>;
  blurred: Blurred;
  setBlurred: Dispatch<SetStateAction<Blurred>>;
  validateField: <T extends OnlineBookingValidation>(
    fieldValid: boolean,
    setFieldValid: Dispatch<SetStateAction<T>>,
    stateKey: keyof T,
    selector: string,
    handleValidationError?: (
      selector: string,
      errorClass: string,
      scroll?: boolean
    ) => Promise<void>
  ) => Promise<void>;
}

/**
 * Contact details validation state management hook.
 *
 * Provides comprehensive validation infrastructure for the contact details form including:
 * - Separate validation state for client and patient fields
 * - Blur tracking to show errors only after user interaction
 * - Form-level validation state
 * - Field refs for programmatic focus management
 * - Generic field validation helper
 *
 * **Validation Strategy:**
 * 1. Fields are validated as user types (real-time)
 * 2. Errors are shown only after:
 *    - Field blur (user leaves field), OR
 *    - Form submission attempt (formValidated = true)
 * 3. Invalid fields are auto-focused on submission failure
 *
 * **Client Fields (Required):**
 * - First Name, Last Name (standard text validation)
 * - Zip Code (format validation)
 * - Identification Number (optional with warning for Swedish users)
 * - Email and Phone Number are validated in the phone verification step
 *
 * **Patient Fields:**
 * - Pet Name (required, text validation)
 * - Pet Species (required, selected from dropdown)
 * - Pet Notes (required, not just whitespace, max length validation)
 *
 * **Refs:**
 * - `petNotesRef` - For focusing notes field on validation error
 * - `identificationNumberRef` - For focusing ID field on validation error
 *
 * @returns Object containing all validation state, setters, refs, and helper functions
 *
 * @example
 * ```tsx
 * const validation = useContactDetailsValidation();
 *
 * // Update client field
 * validation.setClient(prev => ({ ...prev, firstName: 'John' }));
 *
 * // Update validation state
 * validation.setClientValidation(prev => ({
 *   ...prev,
 *   firstNameValid: true
 * }));
 *
 * // Check if form is valid
 * const isValid =
 *   validation.clientValidation.firstNameValid &&
 *   validation.clientValidation.lastNameValid &&
 *   // ... other fields
 *
 * // Validate a field programmatically
 * await validation.validateField(
 *   isFirstNameValid,
 *   validation.setClientValidation,
 *   'firstNameValid',
 *   '#first-name',
 *   handleValidationError
 * );
 *
 * // Focus invalid field
 * if (!validation.clientValidation.firstNameValid) {
 *   validation.identificationNumberRef.current?.focusAsync();
 * }
 * ```
 */
export function useContactDetailsValidation(): UseContactDetailsValidationReturn {
  // Refs for programmatic focus
  const petNotesRef = useRef<TextAreaInputHandle>(null);
  const identificationNumberRef = useRef<TextInputHandle>(null);

  // Client form data
  const [client, setClient] = useState<OnlineBookingClient>({});

  // Patient form data
  const [patient, setPatient] = useState<OnlineBookingPatient>({});

  // Client validation state
  const [clientValidation, setClientValidation] = useState<OnlineBookingClientValidation>(
    {
      firstNameValid: false,
      lastNameValid: false,
      streetAddressValid: true,
      cityValid: true,
      zipCodeValid: true,
      identificationNumberValid: false,
      identificationNumberWarning: false
    }
  );

  // Patient validation state
  const [patientValidation, setPatientValidation] =
    useState<OnlineBookingPatientValidation>({
      petNameValid: false,
      petNotesValid: false,
      petSpeciesValid: false
    });

  // Global form validation state (set to true on submit attempt)
  const [formValidated, setFormValidated] = useState(false);

  // Blur tracking for "touched" fields
  const [blurred, setBlurred] = useState<Blurred>({
    identificationNumber: false,
    firstName: false,
    lastName: false,
    streetAddress: false,
    city: false,
    zipCode: false,
    // email removed - email is validated in phone verification step
    // phoneNumber removed - phone is validated in separate verification step
    petName: false,
    petNotes: false,
    petSpecies: false
  });

  /**
   * Generic field validation helper.
   * Updates validation state and optionally triggers error handling.
   *
   * @param fieldValid - Whether the field is currently valid
   * @param setFieldValid - State setter for the validation object
   * @param stateKey - Property key in the validation object to update
   * @param selector - CSS selector for the field element (for error scrolling)
   * @param handleValidationError - Optional callback for error handling (scroll, focus)
   */
  const validateField = useCallback(
    async <T extends OnlineBookingValidation>(
      fieldValid: boolean,
      setFieldValid: Dispatch<SetStateAction<T>>,
      stateKey: keyof T,
      selector: string,
      handleValidationError?: (
        selector: string,
        errorClass: string,
        scroll?: boolean
      ) => Promise<void>
    ): Promise<void> => {
      setFieldValid(prev => ({ ...prev, [stateKey]: fieldValid }));
      if (!fieldValid && handleValidationError) {
        await handleValidationError(selector, ".validation-error", true);
      }
    },
    []
  );

  return {
    // Refs
    petNotesRef,
    identificationNumberRef,
    // Client fields
    client,
    setClient,
    // Patient fields
    patient,
    setPatient,
    // Validation state
    clientValidation,
    setClientValidation,
    patientValidation,
    setPatientValidation,
    formValidated,
    setFormValidated,
    // Blur state
    blurred,
    setBlurred,
    // Validation helpers
    validateField
  };
}
